import { type FormatContext, panelContext, wallClock } from "@/shared/format";
import type { AgentSession } from "../api/use-agents";

/**
 * AS CONVERSAS POR DIA — Hoje, Ontem, Últimos 7 dias, Mais antigas.
 *
 * Uma lista plana de quarenta linhas com "3 d", "3 d", "4 d" à direita obriga a
 * ler a coluna de datas para achar a fronteira da semana passada; o cabeçalho de
 * grupo faz essa leitura uma vez. É o que a V2 não tinha e o que a lista do
 * ChatGPT/Claude ensinou toda a gente a procurar.
 *
 * O DIA é o do WORKSPACE (`wallClock`), e não o do navegador: uma conversa às
 * 23h de São Paulo é de "hoje" para a equipa inteira, mesmo lida de Lisboa, onde
 * já passou da meia-noite. É a mesma regra de todas as datas do painel.
 *
 * Função pura e fora do componente para o teste poder fixar o "agora".
 */
export type SessionGroup = "today" | "yesterday" | "week" | "older";

const ORDER: readonly SessionGroup[] = ["today", "yesterday", "week", "older"];

/** Dias de calendário entre dois instantes, contados no fuso do workspace. */
export function daysAgo(iso: string, now: Date, ctx: FormatContext = panelContext()): number {
	const day = (value: string | Date): number => {
		const stamp = wallClock(value, ctx).slice(0, 10);
		return stamp ? Date.parse(`${stamp}T00:00:00Z`) : Number.NaN;
	};
	return Math.round((day(now) - day(iso)) / 86_400_000);
}

function groupOf(days: number): SessionGroup {
	if (days <= 0) return "today";
	if (days === 1) return "yesterday";
	if (days < 7) return "week";
	// Uma data ilegível cai aqui: melhor no fim da lista do que fora dela.
	return "older";
}

/** Os grupos NÃO vazios, na ordem do tempo, cada um do mais recente para o mais antigo. */
export function groupSessions(
	sessions: readonly AgentSession[],
	now: Date = new Date(),
	ctx: FormatContext = panelContext(),
): { group: SessionGroup; items: AgentSession[] }[] {
	const sorted = [...sessions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
	const buckets = new Map<SessionGroup, AgentSession[]>();
	for (const session of sorted) {
		const group = groupOf(daysAgo(session.updatedAt, now, ctx));
		buckets.set(group, [...(buckets.get(group) ?? []), session]);
	}
	return ORDER.flatMap((group) => {
		const items = buckets.get(group);
		return items ? [{ group, items }] : [];
	});
}
